/**
 * classifyAssessment(title) — Strict assessment classifier.
 *
 * Returns: { isAssessment: boolean, type: string|null }
 *   type ∈ 'final' | 'midterm' | 'exam' | 'quiz' | 'test'
 *       | 'presentation' | 'project' | 'paper' | null
 *
 * Strategy:
 *   1. Hard DENYLIST — instant reject if title contains junk keywords.
 *   2. Tier 1 ALLOWLIST — always include (midterm/final/quiz/exam/test).
 *   3. Tier 2 ALLOWLIST — include only if a "graded intent" signal is present
 *      (project/presentation/paper/essay/report/lab/deliverable/case).
 *   4. Badge mapping: type is ONLY set to what the title literally contains.
 */

// ── 1. DENYLIST — hard reject ──────────────────────────────────────────────
const DENYLIST = [
  'preparation', 'prepare', 'prep',
  'practice',
  'discussion', 'forum post', 'participation',
  'reading', 'required reading', 'chapter', 'handout', 'articles',
  'bring a', 'example', 'worksheet', 'diagnostic',
  'kickoff', 'kick-off', 'kick off',
  'guest speaker', 'speaker',
  'class meeting',
  'form teams', 'teams formed', 'group formation', 'project teams',
  'office hours',
  'homework', 'admin',
];

// "review session" is denied UNLESS it's "review session for midterm/final/exam"
const REVIEW_SESSION_RE = /\breview\s+session\b/i;
const REVIEW_EXCEPTION_RE = /\breview\s+session\s+(?:for\s+)?(?:midterm|mid-term|final|exam)/i;

// ── 2. TIER 1 — always include (word-boundary match) ───────────────────────
const TIER1 = [
  { type: 'final',   re: /\bfinal(?:s|\s+exam)?\b/i },
  { type: 'midterm', re: /\bmid[-\s]?term\b/i },
  { type: 'quiz',    re: /\bquiz(?:zes)?\b/i },
  { type: 'exam',    re: /\bexam(?:\s+[iv123]+)?\b/i },
  { type: 'test',    re: /\btest\b/i },
];

// ── 3. TIER 2 — include only with graded-intent signal ─────────────────────
const TIER2_CANDIDATES = [
  { type: 'project',      re: /\b(?:project|capstone)\b/i },
  { type: 'presentation', re: /\bpresentation\b/i },
  { type: 'paper',        re: /\b(?:paper|essay|report|term\s+paper)\b/i },
  { type: 'project',      re: /\b(?:lab|deliverable|case)\b/i },
];

const GRADED_SIGNALS = [
  '%', 'points', 'graded', 'grade', 'worth', 'counts for',
  'due', 'submit', 'submission', 'rubric', 'scored',
];

/**
 * @param {string} title
 * @returns {{ isAssessment: boolean, type: string|null }}
 */
export default function classifyAssessment(title) {
  if (!title || typeof title !== 'string') {
    return { isAssessment: false, type: null };
  }

  const lower = title.toLowerCase().trim();

  // ── Step 1: Denylist check ───────────────────────────────────────────────
  for (const deny of DENYLIST) {
    if (lower.includes(deny)) {
      // Exception: if a Tier 1 keyword is ALSO present, allow it through.
      // e.g. "Midterm review session" should still be kept.
      const hasTier1 = TIER1.some(t => t.re.test(lower));
      if (!hasTier1) {
        return { isAssessment: false, type: null };
      }
      break; // has Tier 1 override — skip denylist
    }
  }

  // Special case: "review session" without midterm/final/exam context
  if (REVIEW_SESSION_RE.test(lower) && !REVIEW_EXCEPTION_RE.test(lower)) {
    const hasTier1 = TIER1.some(t => t.re.test(lower));
    if (!hasTier1) {
      return { isAssessment: false, type: null };
    }
  }

  // ── Step 2: Tier 1 match ─────────────────────────────────────────────────
  for (const entry of TIER1) {
    if (entry.re.test(lower)) {
      return { isAssessment: true, type: entry.type };
    }
  }

  // ── Step 3: Tier 2 match (requires graded intent) ────────────────────────
  const hasGradedIntent = GRADED_SIGNALS.some(sig => lower.includes(sig));
  if (hasGradedIntent) {
    for (const entry of TIER2_CANDIDATES) {
      if (entry.re.test(lower)) {
        return { isAssessment: true, type: entry.type };
      }
    }
  }

  // ── No match ─────────────────────────────────────────────────────────────
  return { isAssessment: false, type: null };
}

/** Display label for a given assessment type */
export function assessmentTypeLabel(type) {
  const labels = {
    final: 'Final',
    midterm: 'Midterm',
    exam: 'Exam',
    quiz: 'Quiz',
    test: 'Test',
    presentation: 'Presentation',
    project: 'Project',
    paper: 'Paper',
  };
  return labels[type] || type || 'Assessment';
}

/** Severity index — lower = more severe / higher priority for tie-breaking */
export function assessmentSeverity(type) {
  const order = ['final', 'midterm', 'exam', 'quiz', 'test', 'presentation', 'project', 'paper'];
  const idx = order.indexOf(type);
  return idx === -1 ? 99 : idx;
}

/* ─── Assessment-key normalizer (for dedup / grouping) ─────────────── */

/**
 * Extract ONLY the meaningful differentiator from a title.
 * Since the assessment type (midterm/quiz/exam/…) is already captured
 * separately, we strip everything except numbers that distinguish
 * "Quiz 1" from "Quiz 2".
 *
 * "MIDTERM (in class)"                          → ""
 * "Midterm exam (covers material up to this class)" → ""
 * "In-person Midterm Exam"                       → ""
 * "Quiz 2 - covers material from Ch. 5"          → "2 5"
 * "Final Exam"                                   → ""
 */
export function normalizeTitleCore(title) {
  if (!title) return '';
  // Extract all standalone numbers — these differentiate "Quiz 1" from "Quiz 2"
  const numbers = title.match(/\d+/g);
  return numbers ? numbers.join(' ') : '';
}

/**
 * Produce a grouping key:  courseId :: type :: differentiator
 * Items sharing the same key within ±24 h are treated as one assessment.
 *
 * Because the core now keeps ONLY numbers, all variants like
 * "Midterm", "MIDTERM (in class)", "Midterm exam (covers material…)"
 * collapse to the same key:  courseId::midterm::
 */
export function computeAssessmentKey(courseId, type, title) {
  return `${courseId}::${type}::${normalizeTitleCore(title)}`;
}
